import { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { api } from '../../api';
import { fehlendeFunktion, upgradeAnbieten } from '../../billtoro/upgrade';
import { Dialog } from '../../components/Dialog';
import { fmtDate } from '../../lib/format';
import { useToast } from '../../lib/toast';
import { fehlerText, GesperrtKnopf, tarifFehler } from '../tarifHinweis';
import type { Overview } from '../types';
import { urlId } from '../urlId';
import { artikelText, fruehestes, mengeText, passtArtikel, zaehlungUrl, type Lager, type LagerArtikel } from './LagerDaten';
import './lager.css';

// Zählen (/rechnung/lager/zaehlen, ?artikel=<id> nur dieser Artikel): Zähltag wählen (vom 1. Januar des Vorjahres bis
// heute), je Artikel die gezählte Menge eintragen; leer = nicht gezählt (bleibt, wie es ist). Vor dem Speichern eine
// Zusammenfassung mit den Abweichungen zum Bestand am Ende des Zähltags („laut Billtoro“); die Differenz rechnet der
// Server noch einmal unter der Sperre der Firma und bucht je gezähltem Artikel eine Zählung (auch ohne Abweichung).
// „Zählliste drucken“: Browser-Druck nur mit Nummer, Artikel, Einheit und leerer Spalte (ohne „laut Billtoro“, damit
// ehrlich gezählt wird). Der Stand bis zum Speichern bleibt im Browser (localStorage, nur Komfort).

interface Entwurf {
  datum: string;
  werte: Record<string, string>;
}
const speicherKey = (firmId: string) => `lg-zaehlen:${firmId}`;
function entwurfLesen(firmId: string): Entwurf | null {
  try {
    const raw = localStorage.getItem(speicherKey(firmId));
    const e = raw ? (JSON.parse(raw) as Entwurf) : null;
    return e && typeof e.datum === 'string' && e.werte && typeof e.werte === 'object' ? e : null;
  } catch {
    return null;
  }
}
function entwurfSchreiben(firmId: string, e: Entwurf | null) {
  try {
    if (e && Object.values(e.werte).some((v) => v.trim())) localStorage.setItem(speicherKey(firmId), JSON.stringify(e));
    else localStorage.removeItem(speicherKey(firmId));
  } catch {
    // ohne Speicher: nur kein Wiederherstellen
  }
}

// gezählte Menge: „0“, „12“, „2,5“ (bis 3 Nachkommastellen); leer → null, ungültig → undefined
function gezaehlt(text: string): number | null | undefined {
  const t = text.trim().replace(',', '.');
  if (!t) return null;
  return /^\d+(\.\d{1,3})?$/.test(t) && Number(t) < 1e6 ? Number(t) : undefined;
}

export function Zaehlen({ data, lager }: { data: Overview; lager: Lager }) {
  const toast = useToast();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const firmId = data.firm.id;
  const heute = data.today;
  const nur = urlId(params.get('artikel'));
  const [start] = useState(() => entwurfLesen(firmId));
  const [datum, setDatum] = useState(() => (start && start.datum >= fruehestes(heute) && start.datum <= heute ? start.datum : heute));
  const [werte, setWerte] = useState<Record<string, string>>(() => start?.werte ?? {});
  // Bestand am Ende des Zähltags je Artikel
  const [am, setAm] = useState<Map<string, number> | null>(null);
  const [fehler, setFehler] = useState<string | null>(null);
  const [q, setQ] = useState('');
  const [offen, setOffen] = useState(false);
  const [pruefen, setPruefen] = useState(false);
  const [busy, setBusy] = useState(false);
  const fehlt = fehlendeFunktion(data.tarif, 'lager');
  const datumOk = !!datum && datum >= fruehestes(heute) && datum <= heute;

  useEffect(() => {
    if (!datumOk) return;
    let live = true;
    setAm(null);
    api
      .get<{ artikel: { id: string; bestandAm: number }[] }>(zaehlungUrl(firmId, datum))
      .then((r) => live && setAm(new Map(r.artikel.map((a) => [a.id, a.bestandAm]))))
      .catch((err) => live && setFehler(fehlerText(err)));
    return () => {
      live = false;
    };
  }, [firmId, datum, datumOk]);

  // Stand merken (Zähltag und Eingaben)
  useEffect(() => {
    entwurfSchreiben(firmId, { datum, werte });
  }, [firmId, datum, werte]);

  const alle = useMemo(() => (lager.liste?.artikel ?? []).filter((a) => !nur || a.id === nur), [lager.liste, nur]);
  const gezaehlte = alle.filter((a) => gezaehlt(werte[a.id] ?? '') != null);
  const ungueltig = alle.filter((a) => gezaehlt(werte[a.id] ?? '') === undefined);
  const shown = alle.filter((a) => passtArtikel(a, q) && (!offen || gezaehlt(werte[a.id] ?? '') === null));
  const abweichungen = gezaehlte
    .map((a) => ({ a, diff: Math.round(((gezaehlt(werte[a.id]!) as number) - (am?.get(a.id) ?? 0)) * 1000) / 1000 }))
    .filter((x) => x.diff !== 0);

  function speichern() {
    setFehler(null);
    if (ungueltig.length) return setFehler(`Bitte die Menge prüfen bei ${ungueltig[0]!.name}, z. B. 12 oder 2,5.`);
    if (!gezaehlte.length) return setFehler('Bitte bei mindestens einem Artikel die gezählte Menge eintragen.');
    if (!datumOk) return setFehler(`Bitte einen Zähltag vom ${fmtDate(fruehestes(heute))} bis heute wählen.`);
    setPruefen(true);
  }

  async function bestaetigen() {
    setBusy(true);
    try {
      const r = await api.post<{ gezaehlt: number; abweichungen: unknown[] }>(zaehlungUrl(firmId), {
        datum,
        zeilen: gezaehlte.map((a) => ({ artikelId: a.id, gezaehlt: gezaehlt(werte[a.id]!) })),
      });
      // gemerkt bleiben nur Eingaben, die nicht gespeichert wurden (z. B. andere Artikel beim Zählen eines Artikels)
      const fertig = new Set(gezaehlte.map((a) => a.id));
      entwurfSchreiben(firmId, { datum, werte: Object.fromEntries(Object.entries(werte).filter(([id]) => !fertig.has(id))) });
      toast(`${artikelText(r.gezaehlt)} gezählt${r.abweichungen.length ? `, ${r.abweichungen.length} mit Abweichung` : ''}`);
      void lager.laden();
      navigate(nur ? `/rechnung/lager/${nur}` : '/rechnung/lager');
    } catch (err) {
      setPruefen(false);
      if (!tarifFehler(err)) setFehler(fehlerText(err));
    } finally {
      setBusy(false);
    }
  }

  const speichernKnopf = fehlt ? (
    <GesperrtKnopf tarif={data.tarif} fehlt={fehlt} className="btn primary" onUpgrade={() => upgradeAnbieten(data.tarif, 'lager')}>
      Speichern
    </GesperrtKnopf>
  ) : (
    <button type="button" className="btn primary" onClick={speichern}>
      Speichern ({gezaehlte.length} gezählt)
    </button>
  );

  return (
    <div className="lg lg-zaehlen">
      <p className="lg-zurueck">
        <Link to={nur ? `/rechnung/lager/${nur}` : '/rechnung/lager'}>← {nur ? 'Verlauf' : 'Lager'}</Link>
      </p>
      <div className="rt-head">
        <div>
          <h1>Zählen</h1>
          <p className="muted">Leer lassen, was Sie nicht gezählt haben: dort bleibt der Bestand, wie er ist.</p>
        </div>
        <div className="rt-head-act">
          <button type="button" className="btn" onClick={() => window.print()}>
            Zählliste drucken
          </button>
        </div>
      </div>

      <div className="lg-z-kopf">
        <label className="field lg-z-tag">
          <span>Zähltag</span>
          <input type="date" value={datum} min={fruehestes(heute)} max={heute} onChange={(e) => setDatum(e.target.value)} />
        </label>
        {alle.length > 1 && (
          <>
            <input
              type="search"
              className="rt-search"
              value={q}
              placeholder="Suchen: Name oder Nummer"
              aria-label="Artikel suchen"
              onChange={(e) => setQ(e.target.value)}
            />
            <label className="lg-z-offen">
              <input type="checkbox" checked={offen} onChange={(e) => setOffen(e.target.checked)} /> Nur noch nicht gezählt
            </label>
          </>
        )}
      </div>

      {fehler && (
        <div className="notice bad" role="alert">
          {fehler}
        </div>
      )}
      {!lager.liste ? (
        lager.fehler ? <div className="notice bad">{lager.fehler}</div> : <div className="spinner" aria-label="Lädt" />
      ) : alle.length === 0 ? (
        <div className="rt-card rt-empty">
          <p>Noch kein Artikel mit Bestand. Setzen Sie unter Leistungen und Artikel den Haken „Bestand führen“.</p>
          <Link to="/rechnung/leistungen" className="btn">
            Zu Leistungen und Artikel
          </Link>
        </div>
      ) : (
        <>
          <ul className="lg-z-liste" aria-label="Artikel zum Zählen">
            {shown.map((a) => (
              <ZaehlZeile
                key={a.id}
                a={a}
                am={am?.get(a.id)}
                wert={werte[a.id] ?? ''}
                onChange={(v) => setWerte((cur) => ({ ...cur, [a.id]: v }))}
              />
            ))}
            {shown.length === 0 && <li className="muted lg-z-leer">Kein Artikel passt zu dieser Auswahl.</li>}
          </ul>
          <div className="lg-z-leiste">
            <span className="muted">
              {artikelText(gezaehlte.length)} von {alle.length} gezählt
            </span>
            {speichernKnopf}
          </div>
        </>
      )}

      <table className="lg-druck" aria-hidden="true">
        <caption>
          Zählliste {data.firm.name} · Zähltag {datumOk ? fmtDate(datum) : '__________'}
        </caption>
        <thead>
          <tr>
            <th>Nummer</th>
            <th>Artikel</th>
            <th>Einheit</th>
            <th>Gezählt</th>
          </tr>
        </thead>
        <tbody>
          {alle.map((a) => (
            <tr key={a.id}>
              <td>{a.artikelnummer ?? ''}</td>
              <td>{a.name}</td>
              <td>{a.unit}</td>
              <td />
            </tr>
          ))}
        </tbody>
      </table>

      <Dialog open={pruefen} onClose={() => setPruefen(false)} title="Zählung speichern?" className="lg-dlg-klein">
        <p>
          {artikelText(gezaehlte.length)} gezählt am {fmtDate(datum)}
          {abweichungen.length ? `, ${abweichungen.length} mit Abweichung:` : ', ohne Abweichung.'}
        </p>
        {abweichungen.length > 0 && (
          <ul className="lg-abw">
            {abweichungen.slice(0, 20).map(({ a, diff }) => (
              <li key={a.id}>
                {a.name} <b className="num">{`${diff > 0 ? '+' : ''}${mengeText(diff, a.unit)}`}</b>
              </li>
            ))}
            {abweichungen.length > 20 && <li className="muted">und {abweichungen.length - 20} weitere</li>}
          </ul>
        )}
        <p className="muted lg-klein">Billtoro bucht je Artikel die Differenz zum Bestand am Ende des Zähltags.</p>
        <div className="dlg-actions">
          <button type="button" className="btn" onClick={() => setPruefen(false)}>
            Zurück
          </button>
          <button type="button" className="btn primary" disabled={busy} onClick={() => void bestaetigen()}>
            Bestätigen
          </button>
        </div>
      </Dialog>
    </div>
  );
}

function ZaehlZeile({ a, am, wert, onChange }: { a: LagerArtikel; am: number | undefined; wert: string; onChange(v: string): void }) {
  const falsch = gezaehlt(wert) === undefined;
  return (
    <li className="lg-z-zeile">
      <span className="lg-z-was">
        <b>{a.name}</b>
        <span className="muted">
          {a.artikelnummer && <span className="num">{a.artikelnummer} · </span>}
          laut Billtoro <span className="num">{am === undefined ? '…' : mengeText(am, a.unit)}</span>
        </span>
      </span>
      <label className="lg-z-feld">
        <span className="sr-only">Gezählt {a.name}</span>
        <input
          value={wert}
          inputMode="decimal"
          placeholder="gezählt"
          aria-invalid={falsch || undefined}
          onChange={(e) => onChange(e.target.value)}
        />
        <span className="muted">{a.unit}</span>
      </label>
    </li>
  );
}
